import {
	Highlight,
	HighlightedText,
	IconAndText,
	PersonAvatar,
	PipeSeparator
} from "client/Components";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { ContactGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ReactElement, useMemo } from "react";
import { ItemLayout, AdvancedSearchItemLayoutAction } from "./ItemLayout";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { contactSourceToSourceOptionMap } from "client/Features/AddressBook/SourceSelector";
import { ContactSource } from "client/Api/Email/contacts";
import { localize } from "client_legacy/ndaw/culture";
import { useCommunicationActions } from "client/Features/AddressBook/useCommunicationActions";
import { presenceToAvatarStatusMap } from "client/Features/AddressBook/PersonCard/PersonCard";
import { useContactNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useContactNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type ContactItemViewProps = {
	item: ContactGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	onOpenContactDetails: (id: string, contactSource: ContactSource) => void;
};

export const ContactItemView = (props: ContactItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		onOpenContactDetails
	} = props;

	const {
		contact,
		contactSource
	} = item;

	const { id } = contact;

	const executeHandler: () => void = useContactNavigationHandler({
		contactSource,
		id,
		itemScope,
		searchResult: item,
		onOpenContactDetails: onOpenContactDetails
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("contact-item", `|${contactSource}/${id}`)}
		>
			<ContactItem
				item={item}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
ContactItemView.displayName = "ContactItemView";

type ContactItemProps = {
	item: ContactGlobalSearchResultItem;
	navigationIndex: number;
	onExecute: () => void;
};

const ContactItem = (props: ContactItemProps): ReactElement => {
	const {
		item,
		navigationIndex,
		onExecute
	} = props;

	const {
		hitHighlights,
		contact,
		contactSource
	} = item;

	const {
		jobTitle,
		department,
		officeLocation,
		photoUrl,
		emailAddress,
		phoneNumber,
		presenceStatus
	} = contact;

	const {
		sendEmail,
		makePhoneCall,
		startTeamsChat,
		startTeamsVideoCall
	} = useCommunicationActions({
		emailAddress,
		phoneNumber
	});

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const contactHighlights = useMemo(
		(): Map<string, Highlight> => {
			return fromArray(
				[...highlights.entries()]
					.filter(([key]) => key.startsWith("contact.")),
				([key]) => key.slice("contact.".length),
				([_, value]) => value
			);
		},
		[highlights]
	);

	const {
		computedDisplayName,
		initials,
		highlight: computedDisplayNameHighlight
	} = getComputedDisplayName(contact, contactHighlights);

	const actions = useMemo(
		(): AdvancedSearchItemLayoutAction[] => {
			const actions: AdvancedSearchItemLayoutAction[] = [];

			if (isNotNil(sendEmail)) {
				actions.push({
					id: "email",
					icon: "envelope",
					onClick: sendEmail
				});
			}

			if (isNotNil(makePhoneCall)) {
				actions.push({
					id: "phone-call",
					icon: "phone",
					onClick: makePhoneCall
				});
			}

			if (isNotNil(startTeamsVideoCall)) {
				actions.push({
					id: "video-call",
					icon: "video",
					onClick: startTeamsVideoCall
				});
			}

			if (isNotNil(startTeamsChat)) {
				actions.push({
					id: "chat",
					icon: "comment",
					onClick: startTeamsChat
				});
			}

			return actions;
		},
		[
			makePhoneCall,
			sendEmail,
			startTeamsChat,
			startTeamsVideoCall
		]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="contact-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<PersonAvatar
					trackingId="icon"
					initials={initials}
					imageSrc={photoUrl}
					size="small"
					status={{
						kind: "status",
						status: presenceToAvatarStatusMap[presenceStatus]
					}}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title trackingId="title">
					<HighlightedText
						text={computedDisplayName}
						highlight={computedDisplayNameHighlight}
					/>
				</ItemLayout.Title>
				<ItemLayout.Info>
					{isNotNilOrEmpty(jobTitle) && (
						<span
							{...setTrackingId("job-title")}
						>
							<HighlightedText
								text={jobTitle}
								highlight={contactHighlights.get("jobTitle")}
							/>
							<PipeSeparator />
						</span>
					)}
					{isNotNilOrEmpty(department) && (
						<span
							{...setTrackingId("department")}
						>
							<HighlightedText
								text={department}
								highlight={contactHighlights.get("department")}
							/>
							<PipeSeparator />
						</span>
					)}
					{isNotNilOrEmpty(officeLocation) && (
						<span
							{...setTrackingId("office-location")}
						>
							<HighlightedText
								text={officeLocation}
								highlight={contactHighlights.get("officeLocation")}
							/>
							<PipeSeparator />
						</span>
					)}
					{contactSource !== ContactSource.Suggested && (
						<span
							{...setTrackingId("contact-source")}
						>
							<IconAndText
								text={
									localize(
										contactSourceToSourceOptionMap[contactSource].textKey
									)
								}
							>
								<IconAndText.Icon
									size="small"
									value={contactSourceToSourceOptionMap[contactSource].icon}
								/>
							</IconAndText>
						</span>
					)}
				</ItemLayout.Info>
			</ItemLayout.InfoWrapper>
			<ItemLayout.Actions
				actions={actions}
			/>
		</ItemLayout>
	);
};
ContactItem.displayName = "ContactItem";
